import React, { useState } from "react"

import {
  ThemeQuestion,
  ThemeQuestionWithTheme,
  ThemeWithQuestions,
  ThemeWithQuestionsNested,
} from "features/Surveys/CustomSurveys/models"
import { SelectedQuestionsSection, ThemeQuestionsSection } from "./components"
import * as Styles from "./SurveyQuestionsLibrary.styles"

import {
  useThemesWithCustomQuestionsQuery,
} from "graphql/queries/generated/ThemesWithCustomQuestions"

export const SurveyQuestionsLibrary: React.FC<SurveyQuestionsLibraryProps> = (props) => {
  const [ selectedTheme, setSelectedTheme ] = useState<ThemeWithQuestionsNested>()

  const { data: themesWithQuestionsData, loading: areThemesWithQuestionsLoading } = useThemesWithCustomQuestionsQuery()

  const themes = React.useMemo(
    () => filterEmptyThemesAndNestThemeInQuestions(themesWithQuestionsData?.themes || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ themesWithQuestionsData?.themes ],
  )

  return (
    <Styles.QuestionListContainer>
      <ThemeQuestionsSection
        selectedTheme={selectedTheme}
        themes={themes}
        onThemeChange={(id) => selectTheme(id, themes)}
        areThemesLoading={areThemesWithQuestionsLoading}
        selectedQuestions={props.surveyQuestions}
        onQuestionToggled={(question) => toggleQuestion(question, props.surveyQuestions)}
        isReadOnly={props.isReadOnly}
      />
      <SelectedQuestionsSection
        questions={props.surveyQuestions}
        onRemoveQuestion={(questionId) => removeQuestion(questionId, props.surveyQuestions)}
        onQuestionsReorder={props.onChange}
        isReadOnly={props.isReadOnly}
      />
    </Styles.QuestionListContainer>
  )

  function removeQuestion(questionIndex: number, selectedQuestions: ThemeQuestionWithTheme[]): void {
    const newSelectedQuestions = Array.from(selectedQuestions)
    newSelectedQuestions.splice(questionIndex, 1)

    props.onChange(newSelectedQuestions)
  }

  function toggleQuestion(question: ThemeQuestionWithTheme, selectedQuestions: ThemeQuestionWithTheme[]): void {
    const newSelectedQuestions = Array.from(selectedQuestions)

    const questionIndex = selectedQuestions.findIndex((selectedQuestion) => question.id === selectedQuestion.id)

    if (questionIndex !== -1) {
      newSelectedQuestions.splice(questionIndex, 1)
    }
    else {
      newSelectedQuestions.push(question)
    }

    props.onChange(newSelectedQuestions)
  }

  function selectTheme(id: string, themes: ThemeWithQuestionsNested[]) {
    const selectedTheme = themes.find(theme => theme.id.toString() === id)
    setSelectedTheme(selectedTheme)
  }

  function filterEmptyThemesAndNestThemeInQuestions(themes: ThemeWithQuestions[]): ThemeWithQuestionsNested[] {
    return themes.reduce((themesWithQuestions, theme) => {
      if (theme.questions.length === 0) {
        return themesWithQuestions
      }

      themesWithQuestions.push({
        ...theme,
        questions: theme.questions.map((question) => addThemePropertyToQuestion(
          theme,
          question,
        )),
      })

      return themesWithQuestions
    }, Array<ThemeWithQuestions>())
  }

  function addThemePropertyToQuestion(theme: ThemeWithQuestions, question: ThemeQuestion): ThemeQuestionWithTheme {
    return {
      ...question,
      theme,
    }
  }
}

interface SurveyQuestionsLibraryProps {
  isReadOnly: boolean,
  surveyQuestions: ThemeQuestionWithTheme[],
  onChange: (questions: ThemeQuestionWithTheme[]) => void,
}
